import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { UserAvatar } from "../components/UserAvatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FollowButton } from "../components/FollowButton";
import { FollowersModal } from "../components/FollowersModal";
import { MediaCard } from "../components/MediaCard";
import { Users, Clock, Check, ListTodo, XCircle, BookOpen, Film, Tv, Gamepad2, Calendar, Loader2, Filter } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "../i18n";

const API = '/api';

export function PublicProfile({ user: currentUser }) {
  const { t } = useTranslation();
  const mediaTypeFilters = [
    { value: "all", label: t.library.allTypes },
    { value: "movie", label: t.mediaTypesPlural.movie, icon: Film },
    { value: "tv_series", label: t.mediaTypesPlural.tv_series, icon: Tv },
    { value: "book", label: t.mediaTypesPlural.book, icon: BookOpen },
    { value: "video_game", label: t.mediaTypesShort.video_game, icon: Gamepad2 },
  ];
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [statusTab, setStatusTab] = useState("all");
  const [mediaFilter, setMediaFilter] = useState("all");
  const [followersOpen, setFollowersOpen] = useState(false);
  const [followingOpen, setFollowingOpen] = useState(false);

  useEffect(() => { fetchProfile(); }, [username]);
  useEffect(() => { if (profile) fetchLibrary(); }, [profile, statusTab, mediaFilter]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/profile/${username}`);
      setProfile(res.data);
    } catch (err) {
      if (err.response?.status === 404) toast.error(t.social.userNotFound);
      else toast.error(t.common.loading);
    } finally { setLoading(false); }
  };

  const fetchLibrary = async () => {
    setLibLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusTab !== "all") params.set("status", statusTab);
      if (mediaFilter !== "all") params.set("media_type", mediaFilter);
      params.set("limit", "50");
      const res = await axios.get(`${API}/profile/${username}/library?${params}`);
      setEntries(res.data.entries || []);
    } catch { setEntries([]); }
    finally { setLibLoading(false); }
  };

  const handleFollowToggle = (nowFollowing) => {
    setProfile(prev => ({
      ...prev,
      is_following: nowFollowing,
      followers_count: prev.followers_count + (nowFollowing ? 1 : -1),
    }));
  };

  if (loading) return <div className="flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!profile) return <div className="text-center py-12"><p className="text-muted-foreground">{t.social.userNotFound}</p></div>;

  const joinDate = profile.created_at ? new Date(profile.created_at).toLocaleDateString("es-ES", { year: "numeric", month: "long" }) : "";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="public-profile">
      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 md:gap-6">
            <UserAvatar user={profile} size="xl" className="w-20 h-20 md:w-24 md:h-24" />
            <div className="flex-1 text-center sm:text-left">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div>
                  <h1 className="text-2xl font-bold" data-testid="public-profile-name">@{profile.username}</h1>
                  <p className="text-sm text-muted-foreground" data-testid="public-profile-username">@{profile.username}</p>
                </div>
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  {profile.is_self ? (
                    <Button variant="outline" size="sm" asChild><Link to="/profile">{t.social.editProfile}</Link></Button>
                  ) : (
                    <FollowButton username={username} userId={profile.user_id} isFollowing={profile.is_following} isSelf={false} onToggle={handleFollowToggle} />
                  )}
                </div>
              </div>
              {profile.bio && <p className="mt-2 text-sm">{profile.bio}</p>}
              <div className="flex items-center gap-4 mt-3 justify-center sm:justify-start">
                <button onClick={() => setFollowersOpen(true)} className="text-sm hover:underline" data-testid="followers-count">
                  <span className="font-bold">{profile.followers_count}</span> <span className="text-muted-foreground">{t.social.followers}</span>
                </button>
                <button onClick={() => setFollowingOpen(true)} className="text-sm hover:underline" data-testid="following-count">
                  <span className="font-bold">{profile.following_count}</span> <span className="text-muted-foreground">{t.social.following}</span>
                </button>
                {joinDate && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {t.social.memberSince} {joinDate}
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        {[
          { key: "all", label: t.profile.total, count: profile.total_items, color: "text-primary" },
          { key: "completed", label: t.profile.done, count: profile.library_stats?.completed || 0, color: "text-green-500" },
          { key: "in_progress", label: t.profile.active, count: profile.library_stats?.in_progress || 0, color: "text-blue-500" },
          { key: "planned", label: t.statuses.planned, count: profile.library_stats?.planned || 0, color: "text-yellow-500" },
        ].map((s, i) => (
          <motion.div key={s.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
            <Card className="card-hover cursor-pointer h-full" onClick={() => setStatusTab(s.key)}>
              <CardContent className="p-4 text-center">
                <div className={`text-xl md:text-2xl font-bold ${s.color}`}>{s.count}</div>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg md:text-xl font-semibold">{t.social.publicLibrary}</h2>
          <Select value={mediaFilter} onValueChange={setMediaFilter}>
            <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="public-media-filter">
              <Filter className="w-3 h-3 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              {mediaTypeFilters.map(f => (<SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <Tabs value={statusTab} onValueChange={setStatusTab}>
          <TabsList className="grid w-full grid-cols-5 mb-3 h-auto" data-testid="public-status-tabs">
            <TabsTrigger value="all" className="text-xs py-2.5 min-h-[40px]">{t.profile.all}</TabsTrigger>
            <TabsTrigger value="in_progress" className="text-xs py-2.5 min-h-[40px]">
              <Clock className="w-3 h-3 mr-1 hidden sm:block" />{t.statuses.in_progress}
            </TabsTrigger>
            <TabsTrigger value="planned" className="text-xs py-2.5 min-h-[40px]">{t.statuses.planned}</TabsTrigger>
            <TabsTrigger value="completed" className="text-xs py-2.5 min-h-[40px]">
              <Check className="w-3 h-3 mr-1 hidden sm:block" />{t.statuses.completed}
            </TabsTrigger>
            <TabsTrigger value="dropped" className="text-xs py-2.5 min-h-[40px]">{t.statuses.dropped}</TabsTrigger>
          </TabsList>

          <TabsContent value={statusTab} className="mt-0">
            {libLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : entries.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {entries.map((entry, i) => (<MediaCard key={entry.entry_id} entry={entry} showStatus index={i} />))}
              </div>
            ) : (
              <Card className="text-center py-8">
                <CardContent>
                  <BookOpen className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">{t.profile.noItemsYet}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <FollowersModal open={followersOpen} onOpenChange={setFollowersOpen} username={username} mode="followers" displayName={profile.username} />
      <FollowersModal open={followingOpen} onOpenChange={setFollowingOpen} username={username} mode="following" displayName={profile.username} />
    </div>
  );
}

export default PublicProfile;
